import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getCustomerInfo } from '@services/checkoutService';
import { HOW_DID_YOU_HEAR_OPTIONS } from '@components/Checkout/steps/CustomerInfoStep';
import { useCheckoutStore } from '@store/checkoutStore';
import type { CustomerInfoFormValues } from '@t/checkout.types';
import { toE164 } from '@utils/phoneNumberValidation/phoneNumberValidation';

const KNOWN_HEAR_ABOUT_VALUES = new Set(HOW_DID_YOU_HEAR_OPTIONS.map((o) => o.value));

export const useHydrateCustomerInfo = (
  sessionId: string | null,
  savedCustomerInfo: CustomerInfoFormValues | null
): void => {
  const setCustomerInfo = useCheckoutStore((s) => s.setCustomerInfo);

  const { data: existingCustomerInfo } = useQuery({
    queryKey: ['existingCustomerInfo', sessionId],
    queryFn: () => getCustomerInfo(sessionId!),
    enabled: !!sessionId && !savedCustomerInfo,
    retry: false,
  });

  useEffect(() => {
    if (!existingCustomerInfo || savedCustomerInfo) return;

    const raw = existingCustomerInfo.howDidYouHearAboutUs;
    const isOther = !!raw && !KNOWN_HEAR_ABOUT_VALUES.has(raw);

    const hydrated: CustomerInfoFormValues = {
      titleId: existingCustomerInfo.titleId != null ? String(existingCustomerInfo.titleId) : '',
      firstName: existingCustomerInfo.firstName ?? '',
      middleName: existingCustomerInfo.middleName ?? '',
      lastName: existingCustomerInfo.lastName ?? '',
      email: existingCustomerInfo.email ?? '',
      confirmEmail: existingCustomerInfo.email ?? '',
      dateOfBirth: existingCustomerInfo.dateOfBirth ?? '',
      howDidYouHearAboutUs: isOther ? 'Other' : (raw ?? ''),
      howDidYouHearAboutUsOther: isOther ? raw! : '',
      primaryPhoneNumber: toE164(existingCustomerInfo.primaryPhoneNumber),
      primaryPhoneNumberType: existingCustomerInfo.primaryPhoneNumberType ?? '',
      password: existingCustomerInfo.password ?? '',
      confirmPassword: existingCustomerInfo.password ?? '',
      billDelivery: (existingCustomerInfo.billDelivery as 'email' | 'postal') ?? 'email',
      useSameAddress: !existingCustomerInfo.billingAddress,
      billingStreet: existingCustomerInfo.billingAddress?.street ?? '',
      billingUnit: existingCustomerInfo.billingAddress?.unit ?? '',
      billingCity: existingCustomerInfo.billingAddress?.city ?? '',
      billingProvince: existingCustomerInfo.billingAddress?.province ?? '',
      billingPostalCode: existingCustomerInfo.billingAddress?.postalCode ?? '',
      billingCountry: existingCustomerInfo.billingAddress?.country ?? '',
    };

    setCustomerInfo(hydrated);
  }, [existingCustomerInfo, savedCustomerInfo, setCustomerInfo]);
};
